import apiBaseUrl from '../../utils/config.js';

class AuthorizationHoldService {
  constructor() {
    this.baseUrl = `${apiBaseUrl}/authorization-holds`;
  }

  getToken() {
    return localStorage.getItem('jwt');
  }

  getAuthHeaders() {
    const token = this.getToken();

    return {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    };
  }

  async request(endpoint, method = 'GET', body = null) {
    const options = {
      method,
      headers: this.getAuthHeaders(),
    };

    if (body !== null && body !== undefined) {
      options.body = JSON.stringify(body);
    }

    const response = await fetch(`${this.baseUrl}${endpoint}`, options);
    const text = await response.text();

    let data = null;

    if (text) {
      try {
        data = JSON.parse(text);
      } catch {
        data = text;
      }
    }

    if (!response.ok) {
      const message =
        data?.message ||
        data?.error ||
        data ||
        `Request failed with status ${response.status}`;

      throw new Error(message);
    }

    return data;
  }

  async getBoard(doctorId = '') {
    const params = new URLSearchParams();

    if (doctorId !== null && doctorId !== undefined && doctorId !== '') {
      params.append('doctorId', doctorId);
    }

    const query = params.toString();

    return this.request(`/board${query ? `?${query}` : ''}`);
  }

  async getByStatus(status) {
    return this.request(`/status/${encodeURIComponent(status)}`);
  }

  async getByStatuses(statuses = []) {
    const value = Array.isArray(statuses) ? statuses.join(',') : statuses;

    const params = new URLSearchParams({
      statuses: value,
    });

    return this.request(`/statuses?${params.toString()}`);
  }

  async getByPatientId(patientId) {
    return this.request(`/patient/${patientId}`);
  }

  async getByAssignedDoctorId(doctorId) {
    return this.request(`/doctor/${doctorId}`);
  }

  async getById(holdId) {
    return this.request(`/${holdId}`);
  }

  async getPatientActiveHold(patientId) {
    return this.request(`/patient/${patientId}/active`);
  }

  async createHold(payload) {
    return this.request('/create', 'POST', payload);
  }

  async createFromAuth(payload) {
    return this.request('/create-from-auth', 'POST', payload);
  }

  async startNotes(holdId, doctorId) {
    return this.request(`/${holdId}/start-notes`, 'PATCH', {
      doctorId,
    });
  }

  async markNotesComplete(holdId, doctorId, doctorNotes = '') {
    return this.request(`/${holdId}/mark-notes-complete`, 'PATCH', {
      doctorId,
      doctorNotes,
    });
  }

  async sendToAuthorization(holdId, staffId, authorizationNotes = '') {
    return this.request(`/${holdId}/send-to-authorization`, 'PATCH', {
      staffId,
      authorizationNotes,
    });
  }

  async markWaitingForAuthorization(holdId) {
    return this.request(`/${holdId}/waiting-authorization`, 'PATCH');
  }

  async putOnHold(holdId, staffId, notes = '') {
    return this.request(`/${holdId}/put-on-hold`, 'PATCH', {
      staffId,
      notes,
    });
  }

  async resumeHold(holdId, staffId, notes = '') {
    return this.request(`/${holdId}/resume`, 'PATCH', {
      staffId,
      notes,
    });
  }

  async backtrackHold(holdId, staffId, notes = '') {
    return this.request(`/${holdId}/backtrack`, 'PATCH', {
      staffId,
      notes,
    });
  }

  async enterResult(holdId, staffId, resultStatus, resultNotes = '') {
    return this.request(`/${holdId}/result`, 'PATCH', {
      staffId,
      resultStatus,
      resultNotes,
    });
  }

  async skipNoAuthorizationNeeded(holdId, staffId, resultNotes = '') {
    return this.request(`/${holdId}/skip-no-authorization-needed`, 'PATCH', {
      staffId,
      resultNotes,
    });
  }

  async updateDiagnosisForHold(holdId, doctorId, diagnosisSet) {
    return this.request(`/${holdId}/update-diagnosis`, 'PATCH', {
      doctorId,
      diagnosisSet,
    });
  }

  async attachApprovedAuthorization(
    holdId,
    staffId,
    approvedAuthId,
    resultNotes = ''
  ) {
    return this.request(`/${holdId}/attach-approved-auth`, 'PATCH', {
      staffId,
      approvedAuthId,
      resultNotes,
    });
  }

  /*
   * Creates a brand-new approved authorization from this hold.
   *
   * Payload supports:
   * - authCode
   * - startDate
   * - endDate
   * - totalVisits
   * - active
   * - referredBy
   * - resultNotes
   * - diagnosisSet
   * - procedureSet
   */
  async createApprovedAuthorizationFromHold(holdId, payload) {
    return this.request(`/${holdId}/create-approved-auth`, 'PATCH', payload);
  }

  async markSuperbillNotesComplete(superbillId, staffId) {
    return this.request(`/superbill/${superbillId}/mark-notes`, 'PATCH', {
      staffId,
    });
  }

  async updatePendingAuthCode(
    holdId,
    staffId,
    pendingAuthCode,
    authorizationNotes = ''
  ) {
    return this.request(`/${holdId}/pending-auth-code`, 'PATCH', {
      staffId,
      pendingAuthCode,
      authorizationNotes,
    });
  }

  async deleteHold(holdId) {
    return this.request(`/${holdId}`, 'DELETE');
  }
}

export default AuthorizationHoldService;